import {ChangeDetectionStrategy, Component, computed, effect, inject, signal, viewChild} from '@angular/core';
import {TranslatePipe} from '@ngx-translate/core';
import {FormControl} from '@angular/forms';
import {toSignal} from '@angular/core/rxjs-interop';
import {debounceTime} from 'rxjs';
import {CreatePluginRequest, PluginSummary, UpdatePluginRequest} from '../../../../../api';
import {PluginStoreService} from '../../services/plugin-store.service';
import {ElciaButton} from '../../../../components/buttons/button/button';
import {Search} from '../../../../components/search/search';
import {ElciaDialog} from '../../../../components/dialog/dialog';
import {Loader} from '../../../../components/loader/loader';
import {PluginGrid} from '../plugin-grid/plugin-grid';
import {PluginForm} from '../plugin-form/plugin-form';
import {PluginFormValue} from '../../models/plugin-form-value';
import {ConfirmService} from '../../../../services/confirm.service';

@Component({
  selector: 'elcia-plugin-home',
  imports: [TranslatePipe, ElciaButton, Search, ElciaDialog, Loader, PluginGrid, PluginForm],
  templateUrl: './plugin-home.html',
  providers: [PluginStoreService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PluginHome {
  protected readonly store = inject(PluginStoreService);
  private readonly confirmService = inject(ConfirmService);

  pluginForm = viewChild<PluginForm>(PluginForm);
  isLoading = this.store.isLoading;
  isLoadingSelected = this.store.isLoadingSelected;
  plugins = this.store.plugins;

  protected isFormDisplayed = signal(false);
  private openMode = signal<'create' | 'edit'>('create');

  searchControl = new FormControl('', {nonNullable: true});

  private readonly searchValue = toSignal(
    this.searchControl.valueChanges.pipe(debounceTime(300)),
    {initialValue: this.searchControl.value}
  );

  private readonly syncSearch = effect(() => {
    this.store.searchTerm.set(this.searchValue() || null);
  });

  protected isFormSaving = computed(() =>
    this.store.createPluginMutation.isPending() || this.store.updatePluginMutation.isPending()
  );
  protected isEditMode = computed(() => this.openMode() === 'edit');

  protected showForm(): void {
    this.store.createPluginMutation.reset();
    this.openMode.set('create');
    this.store.selectedPluginId.set(null);
    this.isFormDisplayed.set(true);
  }

  protected closeForm(): void {
    this.isFormDisplayed.set(false);
    this.store.selectedPluginId.set(null);
  }

  protected onDialogSave(): void {
    this.pluginForm()?.submit();
  }

  protected onPluginSelect(plugin: PluginSummary): void {
    this.store.updatePluginMutation.reset();
    this.openMode.set('edit');
    this.store.selectedPluginId.set(plugin.id);
  }

  protected onPluginDelete(plugin: PluginSummary): void {
    this.confirmService.confirm({
      title: 'entity.plugin',
      message: plugin.nameFr,
      accept: async () => {
        await this.store.deletePluginMutation.mutateAsync(plugin.id);
      }
    });
  }

  protected deletePlugin(): void {
    const plugin = this.store.selectedPlugin();
    if (!plugin?.id) return;
    this.confirmService.confirm({
      title: 'entity.plugin',
      message: plugin.nameFr,
      accept: async () => {
        await this.store.deletePluginMutation.mutateAsync(plugin.id);
        this.closeForm();
      }
    });
  }

  protected submit(formValue: PluginFormValue): void {
    const isEdition = this.openMode() === 'edit' && formValue.id !== null;

    if (isEdition) {
      const body: UpdatePluginRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
      };
      this.store.updatePluginMutation.mutate({id: formValue.id!, body});
    } else {
      const request: CreatePluginRequest = {
        nameFr: formValue.nameFr,
        nameEn: formValue.nameEn,
        nameNl: formValue.nameNl,
      };
      this.store.createPluginMutation.mutate(request);
    }
  }

  private readonly closeFormOnSuccess = effect(() => {
    const createSuccess = this.store.createPluginMutation.isSuccess();
    const updateSuccess = this.store.updatePluginMutation.isSuccess();
    if (createSuccess || updateSuccess) {
      this.closeForm();
    }
  });

  private readonly openFormOnEdit = effect(() => {
    const selected = this.store.selectedPlugin();
    if (!selected) return;
    this.isFormDisplayed.set(true);
  });
}
